import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader, ShoppingBag, ArrowRight, Phone } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | failed
  const [transaction, setTransaction] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    document.title = "Paiement confirmé — Atelier du Mac";
    if (!sessionId) { setStatus("failed"); return; }
    pollStatus(0);
    // eslint-disable-next-line
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 8) { setStatus("failed"); return; }
    try {
      const res = await fetch(`${BACKEND}/api/checkout/status/${sessionId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTransaction(data);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (data.status === "expired") { setStatus("failed"); return; }
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex flex-col items-center justify-center text-center px-6">
        <Loader className="w-10 h-10 text-[#FF5C00] animate-spin mb-4" />
        <p className="text-white/60 text-sm">Vérification du paiement en cours…</p>
        <p className="text-white/30 text-xs mt-1">Tentative {attempts + 1}/8</p>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex flex-col items-center justify-center text-center px-6">
        <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-3xl font-black text-white mb-3">Paiement non confirmé</h1>
        <p className="text-white/50 max-w-sm mb-8">
          Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-nous.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/" className="flex items-center gap-2 bg-[#FF5C00] text-white font-semibold px-6 py-3 rounded-full">
            Retour à l'accueil
          </Link>
          <a href="tel:+33611367002" className="flex items-center gap-2 border border-white/20 text-white font-semibold px-6 py-3 rounded-full">
            <Phone className="w-4 h-4" /> Nous appeler
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex flex-col items-center justify-center text-center px-6">
      {/* Success animation */}
      <div className="w-24 h-24 rounded-full bg-[#FF5C00]/10 border-2 border-[#FF5C00]/40 flex items-center justify-center mb-6 animate-bounce">
        <CheckCircle className="w-12 h-12 text-[#FF5C00]" />
      </div>

      <h1 className="text-4xl font-black text-white mb-2 tracking-tight">Paiement confirmé !</h1>
      <p className="text-white/50 max-w-sm mb-8">
        Merci pour votre commande. Vous recevrez un email de confirmation et nous vous contacterons prochainement.
      </p>

      {/* Order recap */}
      {transaction && (
        <div className="w-full max-w-sm bg-[#121215] border border-white/10 rounded-2xl p-5 mb-8 text-left">
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-3">
            Récapitulatif
          </p>
          {transaction.items && transaction.items.map((item, i) => (
            <div key={i} className="flex justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
              <span className="text-white/70">{item.name} × {item.quantity}</span>
              <span className="text-white font-semibold">{(item.price * item.quantity).toFixed(0)}€</span>
            </div>
          ))}
          <div className="flex justify-between mt-3 pt-2 border-t border-white/10">
            <span className="font-bold text-white">Total payé</span>
            <span className="text-xl font-black text-[#FF5C00]">{transaction.amount_total?.toFixed(0)}€</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 justify-center">
        <Link
          to="/"
          data-testid="success-home-btn"
          className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white font-semibold px-6 py-3 rounded-full transition-colors"
        >
          Retour à l'accueil <ArrowRight className="w-4 h-4" />
        </Link>
        <a href="tel:+33611367002" className="flex items-center gap-2 border border-white/20 text-white font-semibold px-6 py-3 rounded-full hover:border-white/40 transition-colors">
          <Phone className="w-4 h-4" /> 06 11 36 70 02
        </a>
      </div>

      <p className="text-white/20 text-xs mt-8">
        Référence : {sessionId?.slice(0, 20)}…
      </p>
    </div>
  );
}
